// @ts-nocheck
import { loadUnitsCatalog } from './loadUnitsCatalog';
import { resolveUnitId } from './normalizeUnitString';
import type { ListUnitsEntry, ListUnitsOptions, UnitDefinition } from './types';
import { isUnitDimension } from './types';

function collectAliases(unit: UnitDefinition, locale: string): string[] {
  const primary = locale.split(/[-_]/)[0]?.toLowerCase() ?? 'en';
  const enAliases = unit.aliases.en ?? [];
  const localeAliases = unit.aliases[primary] ?? [];
  const merged = new Set<string>([unit.id, ...enAliases, ...localeAliases]);
  return [...merged];
}

function displayNameFor(unit: UnitDefinition, locale: string): string {
  const primary = locale.split(/[-_]/)[0]?.toLowerCase() ?? 'en';
  return unit.displayNames?.[primary]
    ?? unit.displayNames?.en
    ?? unit.id;
}

export function listUnits(options: ListUnitsOptions = {}): ListUnitsEntry[] {
  const catalog = loadUnitsCatalog();
  const locale = options.locale ?? 'en';
  const dimension = options.dimension;

  if (dimension !== undefined && !isUnitDimension(dimension)) {
    return [];
  }

  const entries: ListUnitsEntry[] = [];
  for (const unit of catalog.unitsById.values()) {
    if (dimension && unit.dimension !== dimension) {
      continue;
    }
    entries.push({
      id: unit.id,
      dimension: unit.dimension,
      displayName: displayNameFor(unit, locale),
      aliases: collectAliases(unit, locale),
    });
  }

  entries.sort((a, b) => a.dimension.localeCompare(b.dimension) || a.id.localeCompare(b.id));
  return entries;
}

export function listUnitsJson(options: ListUnitsOptions = {}): string {
  return JSON.stringify(listUnits(options));
}

/**
 * Canonical ids of units in the same dimension as `unit`, excluding the resolved input.
 * Returns [] when the unit is unknown or empty.
 */
export function listConvertibleUnits(unit: string): string[] {
  const catalog = loadUnitsCatalog();
  const unitId = resolveUnitId(unit, catalog);
  if (!unitId) {
    return [];
  }
  const dimension = catalog.unitsById.get(unitId)?.dimension;
  if (!dimension) {
    return [];
  }
  return listUnits({ dimension })
    .map((entry) => entry.id)
    .filter((id) => id !== unitId);
}

export function listMaterialsJson(locale = 'en'): string {
  const catalog = loadUnitsCatalog();
  const primary = locale.split(/[-_]/)[0]?.toLowerCase() ?? 'en';
  const entries = [...catalog.materialsById.values()].map((material) => ({
    id: material.id,
    displayName: material.id.replace(/_/g, ' '),
    aliases: [
      material.id,
      ...(material.aliases.en ?? []),
      ...(material.aliases[primary] ?? []),
    ],
  }));
  entries.sort((a, b) => a.id.localeCompare(b.id));
  return JSON.stringify(entries);
}
